import { useState, useEffect, useCallback } from "react";
import { Grid, Paper } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { CheckCircle, Cancel, Block } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import NavigationHeader from "components/NavigationHeader";
import { StripedDataGrid } from "components/StripedDataGrid";
import { FilterColumn } from "components/StripedDataGrid/FilterColumn";
import { TitleTable } from "components/TitleTable";
import ConfirmDeleteModal from "components/ConfirmDeleteModal";
import { FloatMenuCrud } from "components/FloatMenuCrud";
import { DashboardStackCustom, StackCustom } from "components/StackCustom/styles";
import { useNavigation } from "contexts/notificationsContext";
import { debounce } from "lodash";
import { TIME_DEBOUNCE } from "hooks/usePagination";
import {
  ITipoAtividade,
  getTodosTiposAtividade,
  excluirTipoAtividade,
  alterarStatusTipoAtividade,
} from "services/modules/tarefas";
import { ModalTipoAtividade } from "./modalCadastroTipoAtividade";

export const TiposAtividade = () => {
  const { openNotification } = useNavigation();
  const [tiposAtividade, setTiposAtividade] = useState<ITipoAtividade[]>([]);
  const [filteredData, setFilteredData] = useState<ITipoAtividade[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);

  const [isOpenModal, setIsOpenModal] = useState<{
    isOpen: boolean;
    data?: ITipoAtividade;
  }>({ isOpen: false });

  const [modalDelete, setModalDelete] = useState<{
    isOpen: boolean;
    id?: number;
  }>({ isOpen: false });

  const [modalDisabled, setModalDisabled] = useState<{
    isOpen: boolean;
    id?: number;
    ativar?: boolean;
  }>({ isOpen: false });

  const [filterColumnsDebounced, setFilterColumnsDebounced] = useState<{
    column?: "nome";
    value?: string;
  }>();

  const debouncedFilterColumn = useCallback(
    debounce((column: "nome", value: string) => {
      setFilterColumnsDebounced({ column, value });
    }, TIME_DEBOUNCE),
    []
  );

  const columns: GridColDef[] = [
    {
      field: "nome",
      headerName: "Nome",
      flex: 1,
      renderHeader: () => (
        <FilterColumn
          label="Nome"
          name="nome"
          onChange={(event) => {
            debouncedFilterColumn("nome", event.target.value);
          }}
        />
      ),
    },
    {
      field: "descricao",
      headerName: "Descrição",
      flex: 1,
      sortable: false,
    },
    {
      field: "ativo",
      headerName: "Ativo",
      sortable: false,
      flex: 0.15,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        return params.row.ativo ? (
          <CheckCircle color="success" />
        ) : (
          <Cancel color="disabled" />
        );
      },
    },
    {
      field: "options",
      sortable: false,
      headerName: "Ações",
      align: "center",
      headerAlign: "center",
      flex: 0.15,
      renderCell: (params: any) => {
        return (
          <FloatMenuCrud
            onEdit={() => setIsOpenModal({ isOpen: true, data: params.row })}
            showDuplicateOption={false}
            deleteButtonText="Excluir"
            onDelete={() =>
              setModalDelete({ isOpen: true, id: params.row.idTipoAtividade })
            }
            otherOptions={[
              {
                icon: params.row.ativo ? <Cancel /> : <CheckCircle />,
                label: params.row.ativo ? "DESATIVAR" : "ATIVAR",
                onClickButton: () =>
                  setModalDisabled({
                    isOpen: true,
                    id: params.row.idTipoAtividade,
                    ativar: !params.row.ativo,
                  }),
                color: params.row.ativo ? "error" : "success",
              },
            ]}
          />
        );
      },
    },
  ];

  const fetchData = async () => {
    setIsLoadingData(true);
    try {
      const result = await getTodosTiposAtividade();
      setTiposAtividade(result);
      setFilteredData(result);
    } catch (error: any) {
      openNotification({
        message: "Não foi possível carregar os Tipos de Atividade",
        type: "error",
      });
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleDelete = async (id?: number) => {
    if (!id) return;
    setIsLoadingDelete(true);
    try {
      const result = await excluirTipoAtividade(id);
      if (result) {
        openNotification({
          message: "Tipo de atividade excluído com sucesso!",
          type: "success",
        });
        await fetchData();
      }
    } catch (error: any) {
      openNotification({
        message: error?.message || "Erro ao excluir tipo de atividade",
        type: "error",
      });
    } finally {
      setIsLoadingDelete(false);
      setModalDelete({ isOpen: false });
    }
  };

  const handleAtivarDesativar = async (id?: number, ativar?: boolean) => {
    if (!id || ativar === undefined) return;
    try {
      const result = await alterarStatusTipoAtividade(id, ativar);
      if (result) {
        openNotification({
          message: `Tipo de atividade ${ativar ? "ativado" : "desativado"} com sucesso!`,
          type: "success",
        });
        await fetchData();
      }
    } catch (error: any) {
      openNotification({
        message: error?.message || "Erro ao alterar status do tipo de atividade",
        type: "error",
      });
    } finally {
      setModalDisabled({ isOpen: false });
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (filterColumnsDebounced?.value) {
      const filtered = tiposAtividade.filter((t) =>
        t.nome.toLowerCase().includes(filterColumnsDebounced.value!.toLowerCase())
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(tiposAtividade);
    }
  }, [filterColumnsDebounced, tiposAtividade]);

  return (
    <DashboardStackCustom>
      <NavigationHeader
        title="TIPOS DE ATIVIDADE"
        label="Tipos de Atividade"
        buttonLabel="Cadastrar"
        buttonOnClick={() => setIsOpenModal({ isOpen: true })}
        hasBackButton={false}
      />
      <Grid container display="flex" alignItems="center">
        <StackCustom>
          <Grid container mb={4}>
            <Grid item xs={12} sm={8} md={12}>
              <TitleTable title="Listagem de tipos de atividade" />
            </Grid>
          </Grid>
          <Paper>
            <StripedDataGrid
              rows={filteredData}
              columns={columns}
              getRowId={(row: ITipoAtividade) => row.idTipoAtividade}
              loading={isLoadingData || isLoadingDelete}
              checkboxSelection={false}
              rowHeight={70}
            />
          </Paper>
        </StackCustom>
      </Grid>

      <ModalTipoAtividade
        isOpen={isOpenModal.isOpen}
        onClose={() => setIsOpenModal({ isOpen: false })}
        refreshData={() => fetchData()}
        dataTipoAtividade={isOpenModal.data}
      />

      <ConfirmDeleteModal
        title="Tem certeza que deseja excluir o Tipo de Atividade?"
        description="Essa operação não pode ser desfeita. Se o tipo possuir vínculos com gatilhos ou tarefas, a exclusão será bloqueada."
        open={modalDelete.isOpen}
        onClose={() => setModalDelete({ isOpen: false })}
        onConfirm={() => handleDelete(modalDelete.id)}
      />

      <ConfirmDeleteModal
        title={`Tem certeza que deseja ${modalDisabled.ativar ? "ativar" : "desativar"} o Tipo de Atividade?`}
        description={
          modalDisabled.ativar
            ? "O tipo de atividade voltará a ficar disponível para seleção."
            : "O tipo de atividade ficará indisponível para seleção em novos gatilhos e tarefas."
        }
        open={modalDisabled.isOpen}
        onClose={() => setModalDisabled({ isOpen: false })}
        onConfirm={() =>
          handleAtivarDesativar(modalDisabled.id, modalDisabled.ativar)
        }
        customButtonConfirm={
          <LoadingButton
            variant="contained"
            size="medium"
            color={modalDisabled.ativar ? "success" : "error"}
            endIcon={modalDisabled.ativar ? <CheckCircle /> : <Block />}
            onClick={() =>
              handleAtivarDesativar(modalDisabled.id, modalDisabled.ativar)
            }
          >
            {modalDisabled.ativar ? "ATIVAR" : "DESATIVAR"}
          </LoadingButton>
        }
      />
    </DashboardStackCustom>
  );
};
